import React, { useEffect, useState, useRef } from "react";
import { Card } from "@components/_Revamp/StyledComponent";
import { styled } from "@linaria/react";
import { useAvivaDispatch, useAvivaSelector } from "@redux/hook";
import { ROUTE_PATH } from "@static/route-paths";
import { useHistory } from "react-router-dom";
import { SCREEN_SIZE } from "@static/screen-size";
import Accordion from "@components/_Revamp/Accordion";
import { css } from "@linaria/core";
import { Text } from "@components/_Shared/Text";
import Bullet from "@components/_Revamp/Bullet";
import Modal from "@components/_Revamp/Modal";
import { updateDeclarationAcceptance } from "@redux/slices/CCP/step6Slice";
import HealthMainContainer from "@components/_Revamp/HealthMainContainer";
import { themeDefault } from "src/theme-revamp";
import Link from "@components/_Revamp/Link";
import { trackOnCTA, trackOnLoad } from "@pages/CCP/Helpers/analyticsHelper";
import { useScreenSize } from "@pages/CCP/Hooks/useScreenSize";

export const HeaderMobile = styled.div<{ marginTop?: string }>`
  font-family: "Open Sans";
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  text-align: center;
  color: #434343;
  font-size: 18px;
  margin: 0 0 24px 0;
  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    font-size: 28px;
    margin-top: ${(prop) => prop.marginTop || "24"}px;
  }
`;

export const Header = styled.div<{ marginTop?: string }>`
  max-width: 100%;
  font-family: "Open Sans";
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  text-align: center;
  color: #434343;
  font-size: 18px;
  margin: 0 auto 24px auto;
  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    font-size: 28px;
    margin-top: ${(prop) => prop.marginTop || "24"}px;
    max-width: 70%;
  }
`;

export const modalPadding = css`
  .modal-body {
    padding: 24px 16px;
  }
  @media screen and (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    .modal-body {
      padding: 36px;
    }
  }
`;

const Description = styled.div`
  font-family: "Open Sans";
  font-style: normal;
  line-height: 140%;
  text-align: center;
  margin: 16px auto 32px auto;
  letter-spacing: 0.015em;
  color: #8a8988;
  font-size: 14px;
  font-weight: 600;
  width: 100%;
  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    font-size: 18px;
  }
`;

export const ModalDescription = styled.div`
  font-style: normal;
  max-width: 588px;
  font-size: 14px;
  line-height: 140%;
  text-align: center;
  margin-top: 16px;
  letter-spacing: 0.015em;
  font-weight: 600;
  color: #8a8988;
`;

const ListComponent = styled.ul`
  li {
    font-style: normal;
    font-weight: 400;
    font-size: 14px;
    line-height: 140%;
    color: #434343;
    a {
      font-size: 14px;
    }
  }
  li:not(:first-child) {
    margin-top: 24px;
  }
`;

export const CustomButton = styled.button<{ filled: boolean }>`
  border: none;
  display: flex;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  height: 52px;
  background: ${(prop) =>
    prop.filled ? themeDefault.colors.primary : themeDefault.colors.white};
  align-items: center;
  text-align: center;
  letter-spacing: 0.08em;
  transition: all 0.2s linear;
  color: ${(prop) =>
    prop.filled ? themeDefault.colors.white : themeDefault.colors.primary};
  cursor: pointer;
  border: 1px solid #883E89;
  border-radius: 100px;
  box-shadow: none;
  max-width: 320px;
  width: 100%;
  @media (hover: hover) {
    &:hover {
      background: #883E89;
      color: #FFFFFF;
    }
  }
  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    font-size: 12px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-bottom: 24px;
  flex-direction: column;
  width: 100%;
  align-items: center;

  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    flex-direction: row;
  }
`;

export const ModalButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 24px;
  margin: 24px 0 0 0;
  flex-direction: column;
  width: 100%;
  align-items: center;

  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    flex-direction: row;
    margin: 56px 0 0 0;
  }
`;

const importantNotes = [
  "You may wish to separately seek advice on the suitability of the Singlife Cancer Cover Plus II policy.",
  "In the event that you choose not to seek advice on the suitability of the Singlife Cancer Cover Plus II policy, you should consider if the Singlife Cancer Cover Plus II policy is suitable for your financial circumstances and needs.",
];

const container_default_css = css`
  margin-bottom: 130px;
  margin-top: 24px;
`;

const title_default_css = css`
  font-family: "Open Sans";
  color: #434343;
  font-style: normal;
  font-weight: 600;
  line-height: 140%;
  font-size: 16px;
  @media screen and (min-width: 512px) {
    width: unset;
  }
  @media (min-width: ${SCREEN_SIZE.mobilesUp}px) {
    font-size: 14px;
  }
`;

const inside_list_style = css`
  font-size: 14px;
  list-style: inside;
  list-style-type: none;
  li {
    margin: 24px 0 0 10px;
  }
`;

const accordionArgs = {
  title: "Other things to note before you proceed",
  theme: themeDefault,
  className: container_default_css,
  titleClassName: title_default_css,
  headerPadding: "24px 24px 24px 0",
  titleFontSize: 14,
  isExpanded: true,
  showBottomLine: true,
  showHeaderLine: true,
  bottomLineColor: "#c4c4c4",
  bottomLineThickness: "1px",
  contentMarginLeft: "0px",
  contentMarginRight: "0px",
  bottomLineClassName: "margin:10px",
  headerBackgroundColor: "#F4F6F9",
  showHeaderBackground: true,
};

function UserDeclaration() {
  const history = useHistory();
  // const isChild = WhomTobuy === "child" ? true : false;
  // const [isJuvinal, setIsJuvinal] = useState(false);
  const dispatch = useAvivaDispatch();
  const [isShowModal, setIsShowModal] = useState(false);
  const DeclarationRef = useRef<null | HTMLDivElement>(null);
  const SpacerRef = useRef<null | HTMLDivElement>(null);
  const myinfotransactionId = useAvivaSelector(
    (state) => state.cancerCoverStep4.myinfoTransactionId
  );
  const isManual = useAvivaSelector(
    (state) => state.cancerCoverStep4.singpassInitiatedOrManual
  );
  const { isMobile } = useScreenSize();
  //AA Page Loading
  useEffect(() => {
    trackOnLoad();
  }, []);

  useEffect(() => {
    if (isShowModal) {
      trackOnLoad({}, "need-help-with-your-declaration");
    }
  }, [isShowModal]);

  return (
    <>
      <div ref={DeclarationRef}>
        <HealthMainContainer
          stepList={[]}
          backButtonPath={ROUTE_PATH.CANCER_COVER_UW_3}
        >
          {isMobile ? null : (
            <Header>
              Before we start the application, please confirm the following:
            </Header>
          )}
          <Card mobilePadding="24px 24px" marginTop="0" opacityValue="uset">
            {!isMobile ? null : (
              <HeaderMobile>
                Before we start the application, please confirm the following:
              </HeaderMobile>
            )}
            <Card
              padding={"24px 64px"}
              opacityValue="uset"
              backgroundColor="none"
              marginTop="0"
            >
              <ListComponent>
                <li>
                  I’m purchasing the plan without any advice from a Financial
                  Adviser Representative and I take responsibility to ensure
                  that this plan suits my needs
                </li>
                <li>I’m a Singapore Citizen or Permanent Resident</li>
                <li>
                  I also declare that I meet at least two (2) of the following
                  statements:
                  <ul className={inside_list_style}>
                    <li>a) I am below 62 years old</li>
                    <li>
                      b) I am proficient in both spoken and written English
                    </li>
                    <li>
                      c) I have attained at least a GCE N-Level or O-Level
                      certification, or an equivalent academic qualification.
                    </li>
                  </ul>
                </li>

                {/* TODO : Age calculate to add the point */}
                {/* {isJuvinal && (
                  <li>
                    I am between 17 to 21 Age Next Birthday and Singlife will
                    request for my parent’s approval for my application.
                  </li>
                )} */}
              </ListComponent>
            </Card>
            <ButtonContainer>
              <CustomButton
                filled={false}
                onClick={() => {
                  dispatch(updateDeclarationAcceptance(false));
                  setIsShowModal(() => {
                    return true;
                  });
                  trackOnCTA({
                    ctaText: "No, I do not confirm",
                    ctaType: "qb:link",
                  });
                }}
              >
                No, I do not confirm
              </CustomButton>
              <CustomButton
                filled={true}
                onClick={() => {
                  // TODO: navigate to review payment page
                  dispatch(updateDeclarationAcceptance(true));
                  if (myinfotransactionId) {
                    if (isManual === "MANUAL") {
                      history.push(
                        `${ROUTE_PATH.CANCER_COVER_MY_INFO}?transaction_id=${myinfotransactionId}&manual_data=available`
                      );
                    } else {
                      history.push(
                        `${ROUTE_PATH.CANCER_COVER_MY_INFO}?transaction_id=${myinfotransactionId}`
                      );
                    }
                  } else {
                    history.push(ROUTE_PATH.CANCER_COVER_SINGPASS);
                  }
                  trackOnCTA({
                    ctaText: "Yes, I confirm",
                    ctaType: "qb:link",
                  });
                }}
              >
                Yes, I confirm
              </CustomButton>
            </ButtonContainer>
          </Card>
          <Accordion {...accordionArgs}>
            <div style={{ paddingBottom: "20px", paddingTop: "20px" }}>
              <Bullet>
                <Text
                  variant="base"
                  fontSize="14px"
                  fontWeight={isMobile ? 500 : 400}
                >
                  You may reach us{" "}
                  <Link
                    fontWeight={600}
                    fontSize={14}
                    theme={themeDefault}
                    variant="button"
                    text={"here"}
                    onClick={() => {
                      history.push(ROUTE_PATH.CANCER_COVER_CONTACT_US);
                      trackOnCTA({
                        ctaText: "here",
                        ctaType: "qb:link",
                      });
                    }}
                  />{" "}
                  for any queries about the product.
                </Text>
              </Bullet>

              {importantNotes.map((note: React.ReactNode, index: number) => (
                <Bullet key={`${index}`}>
                  <Text
                    variant="base"
                    fontSize="14px"
                    fontWeight={isMobile ? 500 : 400}
                  >
                    {note}
                  </Text>
                </Bullet>
              ))}
            </div>
          </Accordion>
        </HealthMainContainer>
      </div>
      <Modal
        onClose={() => {
          setIsShowModal(false);
        }}
        show={isShowModal}
        colseBtnColor={themeDefault.colors.primary}
        className={modalPadding}
      >
        <>
          <Header marginTop={"0"}>Need help with your declaration?</Header>
          <ModalDescription>
            Contact us if you have any concerns or need help with any part of
            the declaration.
          </ModalDescription>
          <ModalButtonContainer>
            <CustomButton
              filled={false}
              onClick={() => {
                setIsShowModal(false);
                history.push(ROUTE_PATH.CANCER_COVER_LANDING_PAGE);
                trackOnCTA({
                  ctaText: "Back to home",
                  ctaType: "qb:link",
                });
              }}
            >
              Back to home
            </CustomButton>
            <CustomButton
              filled={true}
              onClick={() => {
                // dispatch(updateDeclaration(true));
                history.push(ROUTE_PATH.CANCER_COVER_CONTACT_US);
                trackOnCTA({
                  ctaText: "Contact us",
                  ctaType: "qb:link",
                });
              }}
            >
              Contact us{" "}
            </CustomButton>
          </ModalButtonContainer>
        </>
      </Modal>
      <div ref={SpacerRef}></div>
    </>
  );
}
export default UserDeclaration;
